<template>
  <v-card
    v-if="!isDocumentAuthRoute && !isRequestor && isCreate"
    class="user-info pa-4 mb-8 shadow-md border border-grey-lighten-2"
    flat
    rounded
  >
    <div class="pb-1">
      {{ i18n.t('titles.requestor') }}
    </div>
    <div
      class="d-flex align-center flex-wrap requestor-org-name text-body-large text-black"
      data-test-id="requestor-org-name"
    >
      <div
        class="d-flex align-center"
        :data-test-id="`organization-${initiatorCompanyDisplayName}`"
      >
        {{ initiatorCompanyDisplayName }}
        <v-tooltip
          v-model="showVerificationTooltip"
          :aria-label="verificationText"
          :close-delay="1000"
          content-class="tooltip deep-light tooltip-top bg-white"
          location="top"
          open-on-click
        >
          <template #activator="{ props: ActivatorProps }">
            <v-icon
              v-bind="ActivatorProps"
              class="mx-1"
              :color="verificationIconColor"
              :size="14"
            >
              {{ verificationIcon }}
            </v-icon>
          </template>
          <div class="user-info-verification">
            {{ verificationText }}
            <br />
            <a
              class="text-decoration-none text-primary pointer-events-auto"
              :href="String(i18n.t('texts.verification_badge_details.link'))"
              rel="noopener"
              target="_blank"
              >{{ i18n.t('texts.learn_more') }}</a
            >
          </div>
        </v-tooltip>
      </div>
      <span class="flex-shrink-0" data-test-id="requestor-display-name">
        ({{ requestorDisplayName }})
      </span>
      <div
        class="text-body-medium text-grey mb-2"
        :data-test-id="`requestor-email-${requestorEmail}`"
      >
        {{ requestorEmail }}
      </div>
    </div>
  </v-card>
</template>
<script setup lang="ts">
import { useDocumentContext } from '@/composables/useDocumentContext.ts';
import { computed, ref } from 'vue';
import { VerificationState } from '@/types/enums/VerificationState.ts';
import { useI18n } from 'vue-i18n';
import AuthService from '@/services/authService.ts';
import SignatureService from '@/services/signatureService.ts';
import { DocumentStatus } from '@/types/enums/DocumentStatus.ts';

const { isDocumentAuthRoute, currentDocument } = useDocumentContext();
const i18n = useI18n();
const showVerificationTooltip = ref(false);

const initiatorCompanyDisplayName = computed(() => {
  return currentDocument.value?.initiatorCompanyDisplayName || '';
});

const requestorEmail = computed(() => {
  return currentDocument.value?.initiatorDisplayEmail || '';
});

const requestorDisplayName = computed(() => {
  return currentDocument.value?.initiatorDisplayName || '';
});

const verificationIcon = computed(() => {
  return currentDocument.value?.initiatorCompanyVerificationType ===
    VerificationState.STRONG
    ? 'fas fa-shield-check'
    : 'fas fa-shield-xmark';
});

const verificationIconColor = computed(() => {
  return currentDocument.value?.initiatorCompanyVerificationType ===
    VerificationState.STRONG
    ? '#0052ff'
    : 'warning';
});

const verificationText = computed(() => {
  return currentDocument.value?.initiatorCompanyVerificationType ===
    VerificationState.STRONG
    ? i18n.t('texts.verification_badge_details.verified')
    : i18n.t('texts.verification_badge_details.unverified');
});

const isRequestor = computed(() => {
  if (currentDocument.value) {
    return SignatureService.compareString(
      currentDocument.value?.initiatorDisplayEmail,
      AuthService.user?.email
    );
  }
  return false;
});

const isCreate = computed(() => {
  return currentDocument.value?.documentStatus === DocumentStatus.DRAFT;
});
</script>
<style lang="scss" scoped>
.user-info-verification {
  max-width: 184px;
  font-size: 13px;
}

.requestor-org-name {
  font-feature-settings: 'liga' off;
  letter-spacing: 0.005em !important;
}
</style>
